import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  User, 
  CreditCard, 
  CheckCircle2, 
  ArrowLeft, 
  ArrowRight,
  X,
  Euro,
  Plus,
  Minus,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Calendar
} from 'lucide-react';
import { 
  createCompany, 
  createCompanySubscription,
  createVatEntity,
  fetchSubscriptionAddons,
  updateCompanyFeature,
  getFeaturesByTier,
  supabase,
  type Company,
  type SubscriptionAddon
} from '../lib/supabase';

interface CompanyOnboardingProps {
  onComplete: (company: Company) => void;
  onCancel: () => void;
}

interface OnboardingData {
  // Step 1: Company Info
  companyName: string;
  industry: string;
  size: 'small' | 'medium' | 'large' | 'enterprise';
  primaryColor: string;
  logoUrl: string;
  
  // Step 2: Admin User
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string;
  adminRole: string;
  
  // Step 3: Billing & Subscription
  billingCompanyName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  billingAddress: string;
  vatNumber: string;
  paymentTerms: number;
  selectedAddons: string[];
  subscriptionStartDate: string;
  trialPeriod: '3' | '6' | 'none';
  setupFee: number;
  
  // Step 4: Review
}

const INDUSTRIES = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'banking', label: 'Banking' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'technology', label: 'Technology' },
  { value: 'other', label: 'Other' }
];

const COMPANY_SIZES = [
  { value: 'small', label: 'Small (1-50 employees)' },
  { value: 'medium', label: 'Medium (51-250 employees)' },
  { value: 'large', label: 'Large (251-1000 employees)' },
  { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
];

export function CompanyOnboarding({ onComplete, onCancel }: CompanyOnboardingProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addons, setAddons] = useState<SubscriptionAddon[]>([]);
  
  const [data, setData] = useState<OnboardingData>({
    companyName: '',
    industry: '',
    size: 'medium',
    primaryColor: '#e62336',
    logoUrl: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    adminRole: 'CEO',
    billingCompanyName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    billingAddress: '',
    vatNumber: '',
    paymentTerms: 10,
    selectedAddons: [],
    subscriptionStartDate: new Date().toISOString().split('T')[0],
    trialPeriod: '3',
    setupFee: 1500
  });

  useEffect(() => {
    loadAddons();
  }, []);

  const loadAddons = async () => {
    try {
      const addonsData = await fetchSubscriptionAddons();
      setAddons(addonsData);
    } catch (err) {
      console.error('Error loading addons:', err);
    }
  };

  const updateData = (field: keyof OnboardingData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAddon = (addonId: string) => {
    setData(prev => ({
      ...prev,
      selectedAddons: prev.selectedAddons.includes(addonId)
        ? prev.selectedAddons.filter(id => id !== addonId)
        : [...prev.selectedAddons, addonId]
    }));
  };

  const calculateTotalPrice = () => {
    const basePrice = 250; // Basic BizComfort license
    const addonPrice = data.selectedAddons.reduce((total, addonId) => {
      const addon = addons.find(a => a.id === addonId);
      return total + (addon?.monthly_price || 0);
    }, 0);
    return basePrice + addonPrice;
  };

  const canProceedStep1 = data.companyName && data.industry && data.size;
  const canProceedStep2 = data.adminFirstName && data.adminLastName && data.adminEmail && data.adminPhone;
  const canProceedStep3 = data.billingCompanyName && data.billingContactEmail && data.billingContactPhone && data.billingAddress && data.subscriptionStartDate;

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
      
      // Pre-fill billing company name with company name when moving to step 3
      if (currentStep === 2 && !data.billingCompanyName) {
        setData(prev => ({ ...prev, billingCompanyName: prev.companyName }));
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Create company
      const newCompany = await createCompany({
        name: data.companyName,
        industry: data.industry,
        size: data.size,
        subscription_tier: 'professional', // Default to professional
        branding: {
          primary_color: data.primaryColor,
          logo_url: data.logoUrl || undefined
        },
        settings: {
          default_language: 'NL',
          timezone: 'Europe/Brussels',
          currency: 'EUR'
        },
        admin_user_id: null, // Set to null instead of empty string
        is_active: true
      });

      // Set up features based on subscription tier
      const features = getFeaturesByTier('professional');
      for (const featureKey of features) {
        await updateCompanyFeature(newCompany.id, featureKey, true);
      }

      // Create VAT entity
      await createVatEntity({
        company_id: newCompany.id,
        name: data.billingCompanyName,
        vat_number: data.vatNumber || 'BE0000000000',
        address: data.billingAddress,
        is_default: true
      });

      // Create subscription
      await createCompanySubscription({
        company_id: newCompany.id,
        base_monthly_price: 250,
        addon_ids: data.selectedAddons,
        total_monthly_price: calculateTotalPrice(),
        billing_contact_name: data.billingCompanyName,
        billing_contact_email: data.billingContactEmail,
        billing_contact_phone: data.billingContactPhone,
        billing_address: data.billingAddress,
        vat_number: data.vatNumber || undefined,
        payment_terms_days: data.paymentTerms,
        is_active: true
      });

      // Create company admin user via edge function
      try {
        const createAdminResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-company-admin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            companyId: newCompany.id,
            companyName: data.companyName,
            adminFirstName: data.adminFirstName,
            adminLastName: data.adminLastName,
            adminEmail: data.adminEmail,
            adminRole: data.adminRole
          })
        });

        if (!createAdminResponse.ok) {
          const errorData = await createAdminResponse.json();
          console.error('Error creating company admin:', errorData);
          throw new Error(`Failed to create company admin: ${errorData.error || 'Unknown error'}`);
        }
      } catch (adminError) {
        console.error('Error creating admin user:', adminError);
        // Continue with company creation even if admin creation fails
        // We'll handle this separately
      }

      // Fetch complete company data
      const { data: fullCompany, error: fetchError } = await supabase
        .from('companies')
        .select(`
          *,
          features:company_features(*)
        `)
        .eq('id', newCompany.id)
        .single();

      if (fetchError || !fullCompany) {
        throw new Error('Failed to load complete company data');
      }

      onComplete(fullCompany);
    } catch (err: any) {
      console.error('Error creating company:', err);
      setError(err.message || 'Failed to create company');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Company Info', icon: Building2 },
    { number: 2, title: 'Admin User', icon: User },
    { number: 3, title: 'Subscription', icon: CreditCard },
    { number: 4, title: 'Review', icon: CheckCircle2 }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create New Company</h1>
              <p className="text-gray-600 mt-1">Set up a new company account with subscription</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mt-6">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    currentStep >= step.number
                      ? 'bg-jaimy-red text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    <step.icon className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      currentStep >= step.number ? 'text-jaimy-red' : 'text-gray-500'
                    }`}>
                      Step {step.number}
                    </p>
                    <p className={`text-xs ${
                      currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-1 mx-4 ${
                      currentStep > step.number ? 'bg-jaimy-red' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Step 1: Company Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={data.companyName}
                    onChange={(e) => updateData('companyName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    value={data.industry}
                    onChange={(e) => updateData('industry', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="">Select industry</option>
                    {INDUSTRIES.map(industry => (
                      <option key={industry.value} value={industry.value}>
                        {industry.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size *
                  </label>
                  <select
                    value={data.size}
                    onChange={(e) => updateData('size', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    {COMPANY_SIZES.map(size => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={data.primaryColor}
                      onChange={(e) => updateData('primaryColor', e.target.value)}
                      className="h-10 w-16 border border-gray-200 rounded-lg"
                    />
                    <input
                      type="text"
                      value={data.primaryColor}
                      onChange={(e) => updateData('primaryColor', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={data.logoUrl}
                    onChange={(e) => updateData('logoUrl', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Admin User */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Admin User Setup</h2>
              <p className="text-gray-600">This person will receive an email invitation to activate their admin account.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={data.adminFirstName}
                    onChange={(e) => updateData('adminFirstName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={data.adminLastName}
                    onChange={(e) => updateData('adminLastName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={data.adminEmail}
                      onChange={(e) => updateData('adminEmail', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      value={data.adminPhone}
                      onChange={(e) => updateData('adminPhone', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role/Title
                  </label>
                  <input
                    type="text"
                    value={data.adminRole}
                    onChange={(e) => updateData('adminRole', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    placeholder="CEO, CTO, Manager, etc."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Subscription */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Subscription & Billing</h2>
              
              {/* Subscription Details */}
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Base BizComfort License</h3>
                  <p className="text-blue-800 text-sm mb-3">Essential features for service management</p>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-900 font-medium">Monthly subscription</span>
                    <span className="text-2xl font-bold text-blue-900">€250</span>
                  </div>
                </div>

                {/* Subscription Start Date and Trial Period */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Start Date *
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="date"
                        value={data.subscriptionStartDate}
                        onChange={(e) => updateData('subscriptionStartDate', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trial Period
                    </label>
                    <select
                      value={data.trialPeriod}
                      onChange={(e) => updateData('trialPeriod', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="none">No Trial</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                    </select>
                  </div>
                </div>

                {/* Setup Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Setup Fee
                  </label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      value={data.setupFee}
                      onChange={(e) => updateData('setupFee', parseFloat(e.target.value))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">One-time setup fee for account configuration</p>
                </div>
              </div>

              {/* Add-ons */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Add-ons</h3>
                <div className="space-y-3">
                  {addons.map(addon => (
                    <div
                      key={addon.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        data.selectedAddons.includes(addon.id)
                          ? 'border-jaimy-red bg-jaimy-red/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleAddon(addon.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            data.selectedAddons.includes(addon.id)
                              ? 'border-jaimy-red bg-jaimy-red'
                              : 'border-gray-300'
                          }`}>
                            {data.selectedAddons.includes(addon.id) && (
                              <CheckCircle2 className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{addon.name}</h4>
                            <p className="text-sm text-gray-600">{addon.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-semibold text-gray-900">
                            €{addon.monthly_price}
                          </span>
                          <p className="text-xs text-gray-500">per month</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between text-lg font-semibold">
                  <span>Total Monthly Cost</span>
                  <span className="text-2xl text-jaimy-red">€{calculateTotalPrice()}</span>
                </div>
                {data.trialPeriod !== 'none' && (
                  <div className="mt-2 text-sm text-green-600">
                    {data.trialPeriod === '3' ? '3-month' : '6-month'} trial period included
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span>One-time setup fee</span>
                  <span>€{data.setupFee.toFixed(2)}</span>
                </div>
              </div>

              {/* Billing Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Company Name *
                    </label>
                    <input
                      type="text"
                      value={data.billingCompanyName}
                      onChange={(e) => updateData('billingCompanyName', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Email *
                    </label>
                    <input
                      type="email"
                      value={data.billingContactEmail}
                      onChange={(e) => updateData('billingContactEmail', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Phone *
                    </label>
                    <input
                      type="tel"
                      value={data.billingContactPhone}
                      onChange={(e) => updateData('billingContactPhone', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      value={data.vatNumber}
                      onChange={(e) => updateData('vatNumber', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      placeholder="BE0123456789"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Address *
                    </label>
                    <textarea
                      value={data.billingAddress}
                      onChange={(e) => updateData('billingAddress', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      placeholder="Street, City, Postal Code, Country"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Terms (Days)
                    </label>
                    <select
                      value={data.paymentTerms}
                      onChange={(e) => updateData('paymentTerms', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value={10}>10 days</option>
                      <option value={15}>15 days</option>
                      <option value={30}>30 days</option>
                      <option value={45}>45 days</option>
                      <option value={60}>60 days</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Review & Confirm</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Company Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Name:</span> {data.companyName}</div>
                    <div><span className="font-medium">Industry:</span> {INDUSTRIES.find(i => i.value === data.industry)?.label}</div>
                    <div><span className="font-medium">Size:</span> {COMPANY_SIZES.find(s => s.value === data.size)?.label}</div>
                  </div>
                </div>

                {/* Admin User */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Admin User</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Name:</span> {data.adminFirstName} {data.adminLastName}</div>
                    <div><span className="font-medium">Email:</span> {data.adminEmail}</div>
                    <div><span className="font-medium">Phone:</span> {data.adminPhone}</div>
                    <div><span className="font-medium">Role:</span> {data.adminRole}</div>
                  </div>
                </div>

                {/* Subscription */}
                <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                  <h3 className="font-semibold text-gray-900 mb-3">Subscription Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Base BizComfort License</span>
                      <span>€250/month</span>
                    </div>
                    {data.selectedAddons.map(addonId => {
                      const addon = addons.find(a => a.id === addonId);
                      return addon ? (
                        <div key={addonId} className="flex justify-between">
                          <span>{addon.name}</span>
                          <span>€{addon.monthly_price}/month</span>
                        </div>
                      ) : null;
                    })}
                    <div className="border-t pt-2 flex justify-between font-semibold">
                      <span>Total Monthly Cost</span>
                      <span className="text-jaimy-red">€{calculateTotalPrice()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Setup Fee (one-time)</span>
                      <span>€{data.setupFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Subscription Start Date</span>
                      <span>{new Date(data.subscriptionStartDate).toLocaleDateString()}</span>
                    </div>
                    {data.trialPeriod !== 'none' && (
                      <div className="flex justify-between text-green-600">
                        <span>Trial Period</span>
                        <span>{data.trialPeriod} months</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Billing */}
                <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                  <h3 className="font-semibold text-gray-900 mb-3">Billing Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div><span className="font-medium">Billing Company:</span> {data.billingCompanyName}</div>
                      <div><span className="font-medium">Email:</span> {data.billingContactEmail}</div>
                      <div><span className="font-medium">Phone:</span> {data.billingContactPhone}</div>
                    </div>
                    <div>
                      <div><span className="font-medium">VAT Number:</span> {data.vatNumber || 'Not provided'}</div>
                      <div><span className="font-medium">Payment Terms:</span> {data.paymentTerms} days</div>
                      <div><span className="font-medium">Address:</span> {data.billingAddress}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex justify-between">
          <button
            onClick={currentStep === 1 ? onCancel : handlePrevious}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !canProceedStep1) ||
                (currentStep === 2 && !canProceedStep2) ||
                (currentStep === 3 && !canProceedStep3)
              }
              className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={isLoading}
              className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating Company...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Create Company
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}